import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Paper,
    Divider,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { findStebbyTicket, redeemStebbyTicket, getAffiliates } from '../api/stebbyApi';

const StebbyModal = ({ open, onClose }) => {
    const [ticketCode, setTicketCode] = useState('');
    const [selectedAffiliate, setSelectedAffiliate] = useState('');
    const [affiliates, setAffiliates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAffiliates, setLoadingAffiliates] = useState(false);
    const [error, setError] = useState('');
    const [ticketInfo, setTicketInfo] = useState(null);
    const [redeemed, setRedeemed] = useState(false);
    const [scheduleRedemption, setScheduleRedemption] = useState(false);
    const [scheduledDate, setScheduledDate] = useState('');

    // Load affiliates when modal opens
    useEffect(() => {
        if (open) {
            loadAffiliates();
        }
    }, [open]);

    // Get tomorrow's date in YYYY-MM-DD format for min date
    const getTomorrowDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };

    // Load available affiliates
    const loadAffiliates = async () => {
        setLoadingAffiliates(true);
        try {
            const response = await getAffiliates();
            if (response.success) {
                setAffiliates(response.affiliates);
            } else {
                setError('Failed to load affiliate data');
            }
        } catch (error) {
            setError('Error loading affiliate data');
        } finally {
            setLoadingAffiliates(false);
        }
    };

    // Reset state when modal closes
    const handleClose = () => {
        setTicketCode('');
        setSelectedAffiliate('');
        setError('');
        setTicketInfo(null);
        setRedeemed(false);
        setScheduleRedemption(false);
        setScheduledDate('');
        onClose();
    };

    // Find ticket
    const handleFindTicket = async () => {
        setError('');

        if (!selectedAffiliate) {
            setError('Please select an affiliate');
            return;
        }

        if (!ticketCode) {
            setError('Please enter the ticket code');
            return;
        }

        setLoading(true);

        try {
            const response = await findStebbyTicket(ticketCode, selectedAffiliate);

            if (response.success) {
                setTicketInfo(response.ticket);
            } else {
                setError(response.message || 'Ticket not found');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error finding ticket. Please check the code and try again.');
        } finally {
            setLoading(false);
        }
    };

    // Redeem ticket
    const handleRedeemTicket = async () => {
        setError('');

        // Validate scheduled date if checkbox is checked
        if (scheduleRedemption && !scheduledDate) {
            setError('Please select a date for scheduled redemption');
            return;
        }

        setLoading(true);

        try {
            const response = await redeemStebbyTicket(
                ticketCode,
                selectedAffiliate,
                scheduleRedemption ? scheduledDate : null
            );

            if (response.success) {
                setRedeemed(true);
                // Auto-close modal after 3 seconds
                setTimeout(() => {
                    // refresh page
                    window.location.reload();
                    handleClose();
                }, 3000);
            } else {
                setError(response.message || 'Failed to redeem ticket');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error redeeming ticket. Ticket may already be used.');
        } finally {
            setLoading(false);
            setTicketInfo(null);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && ticketCode && selectedAffiliate && !loading && !ticketInfo) {
            handleFindTicket();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ConfirmationNumberIcon color="primary" />
                    <Typography variant="h6">Redeem Stebby Ticket</Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pb: 2 }}>
                <Divider sx={{ mb: 3 }} />

                {/* Success state */}
                {redeemed ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircleOutlineIcon
                            sx={{ fontSize: 60, color: 'success.main', mb: 2 }}
                        />
                        <Typography variant="h6" gutterBottom>
                            Ticket successfully redeemed!
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            The ticket has been marked as used.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Affiliate and ticket input */}
                        {!ticketInfo && (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Select an affiliate and enter your Stebby ticket code.
                                </Typography>

                                {/* Affiliate selection */}
                                <FormControl fullWidth margin="normal" disabled={loading || loadingAffiliates}>
                                    <InputLabel>Affiliate</InputLabel>
                                    <Select
                                        value={selectedAffiliate}
                                        onChange={(e) => setSelectedAffiliate(e.target.value)}
                                        label="Affiliate"
                                        startAdornment={<BusinessIcon sx={{ mr: 1, color: 'action.active' }} />}
                                    >
                                        {affiliates.map((affiliate) => (
                                            <MenuItem key={affiliate.id} value={affiliate.id}>
                                                {affiliate.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Ticket code input */}
                                <TextField
                                    fullWidth
                                    label="Ticket Code"
                                    value={ticketCode}
                                    onChange={(e) => setTicketCode(e.target.value.toUpperCase())}
                                    onKeyPress={handleKeyPress}
                                    margin="normal"
                                    disabled={loading}
                                    placeholder="VVKAHDKLASKJA"
                                    InputProps={{
                                        startAdornment: <ConfirmationNumberIcon sx={{ mr: 1, color: 'action.active' }} />
                                    }}
                                    helperText="Enter the ticket code you received from Stebby"
                                />
                            </Box>
                        )}

                        {/* Ticket info */}
                        {ticketInfo && !redeemed && (
                            <Box>
                                <Paper elevation={1} sx={{ p: 3, bgcolor: 'background.default' }}>
                                    <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                        Ticket Details
                                    </Typography>

                                    <Box sx={{ mt: 2 }}>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                <PersonIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                                Customer
                                            </Typography>
                                            <Typography variant="body1" fontWeight={500}>
                                                {ticketInfo.client.name}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Service
                                            </Typography>
                                            <Typography variant="h6" color="primary">
                                                {ticketInfo.purchasable.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                <Chip
                                                    label={`€${ticketInfo.purchasable.price}`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    label={ticketInfo.purchasable.category}
                                                    size="small"
                                                    icon={<CategoryIcon />}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Ticket Code
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                                {ticketInfo.code}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Valid Until
                                            </Typography>
                                            <Typography variant="body1" color="success.main" fontWeight={500}>
                                                {formatDate(ticketInfo.validUntil)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>

                                {/* Schedule redemption option */}
                                <Box sx={{ mt: 3 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={scheduleRedemption}
                                                onChange={(e) => {
                                                    setScheduleRedemption(e.target.checked);
                                                    if (!e.target.checked) {
                                                        setScheduledDate('');
                                                    }
                                                }}
                                                disabled={loading}
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ScheduleIcon sx={{ fontSize: 18 }} />
                                                <Typography variant="body2">
                                                    Schedule redemption for later
                                                </Typography>
                                            </Box>
                                        }
                                    />

                                    {/* Date picker - shown only when checkbox is checked */}
                                    {scheduleRedemption && (
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Redemption Date"
                                            value={scheduledDate}
                                            onChange={(e) => setScheduledDate(e.target.value)}
                                            margin="normal"
                                            disabled={loading}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            inputProps={{
                                                min: getTomorrowDate()
                                            }}
                                            helperText="Select a future date for ticket redemption"
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* Error message */}
                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Loading affiliates */}
                        {loadingAffiliates && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    Loading affiliates...
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                {!redeemed && !ticketInfo && (
                    <>
                        <Button onClick={handleClose} disabled={loading || loadingAffiliates}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleFindTicket}
                            variant="contained"
                            disabled={!ticketCode || !selectedAffiliate || loading || loadingAffiliates}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                            Find Ticket
                        </Button>
                    </>
                )}

                {!redeemed && ticketInfo && (
                    <>
                        <Button
                            onClick={() => {
                                setTicketInfo(null);
                                setTicketCode('');
                                setError('');
                                setScheduleRedemption(false);
                                setScheduledDate('');
                            }}
                            disabled={loading}
                        >
                            Back
                        </Button>
                        <Button
                            onClick={handleRedeemTicket}
                            variant="contained"
                            color="success"
                            disabled={loading || (scheduleRedemption && !scheduledDate)}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                            {scheduleRedemption ? 'Schedule Redemption' : 'Redeem Ticket'}
                        </Button>
                    </>
                )}

                {redeemed && (
                    <Button onClick={handleClose} variant="contained">
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default StebbyModal;